import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  GoogleMap,
  Marker,
  InfoWindow,
  useJsApiLoader,
  MarkerClustererF,
} from "@react-google-maps/api";
import { Users, MapPin, UserPlus, LocateFixed, XCircle, Info } from "lucide-react";
import { Translator } from "./translator";

// CONFIG
const BASE = "https://xmrhe4snn1.execute-api.eu-north-1.amazonaws.com";
const GOOGLE_MAPS_API_KEY = "AIzaSyCOa-Vx3796Zb7OSqUu9qknCyKPwDY6H28";

const DEFAULT_CENTER = { lat: 41.9028, lng: 12.4964 }; // Roma
const containerStyle = { width: "100%", height: "100%" }; // To control height via wrappers

// UTILITIES
// Throttle helper (leading + trailing)
function throttle(fn, ms) {
  let last = 0, timer = null, lastArgs = null;
  return (...args) => {
    const now = Date.now();
    const remaining = ms - (now - last);
    lastArgs = args;
    if (remaining <= 0) {
      last = now;
      fn(...args);
    } else if (!timer) {
      timer = setTimeout(() => {
        last = Date.now();
        timer = null;
        fn(...(lastArgs || []));
      }, remaining);
    }
  };
}

// Simple debounce for inputs
function useDebounced(value, delay = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

// Haversine distance (meters)
function distanceMeters(a, b) {
  if (!a || !b) return Infinity;
  const R = 6371000;
  const dLat = (b.lat - a.lat) * Math.PI/180;
  const dLng = (b.lng - a.lng) * Math.PI/180;
  const lat1 = a.lat * Math.PI/180;
  const lat2 = b.lat * Math.PI/180;
  const x = Math.sin(dLat/2)**2 + Math.cos(lat1)*Math.cos(lat2)*Math.sin(dLng/2)**2;
  return 2 * R * Math.asin(Math.sqrt(x));
}

// COMPONENT
export default function PeopleMap() {
  // iOS momentum scroll + hide scrollbars
  useEffect(() => {
    const css = `
      .ios-scroll { -webkit-overflow-scrolling: touch; }
      .scrollbar-none { scrollbar-width: none; }
      .scrollbar-none::-webkit-scrollbar { display: none; }
      @supports (height: 100svh) {
        .h-svh { height: 100svh; }
      }
    `;
    const style = document.createElement("style");
    style.id = "peoplemap-inline-css";
    style.appendChild(document.createTextNode(css));
    document.head.appendChild(style);
    return () => { try { document.head.removeChild(style); } catch {} };
  }, []);

  const { translateText, lang } = Translator();

  const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER);
  const [me, setMe] = useState(null); // {lat,lng,accuracy}
  const [sharing, setSharing] = useState(false);
  const [neighbors, setNeighbors] = useState([]); // [{email,lat,lng,name,image,city,admin1,country}]
  const [activeUser, setActiveUser] = useState(null);
  const [radiusKm, setRadiusKm] = useState(10);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null); // {type:'error'|'info'|'success', message}
  const [sortBy, setSortBy] = useState("nearest"); // 'nearest'|'name'|'none'
  const [cityFilter, setCityFilter] = useState("");
  const [nameQuery, setNameQuery] = useState("");
  const debouncedQuery = useDebounced(nameQuery, 250);

  const circleRef = useRef(null);
  const mapRef = useRef(null);
  const didCenterRef = useRef(false);
  const watchingRef = useRef(false);

  const onMapLoad = (m) => { mapRef.current = m; };
  const onMapUnmount = () => { mapRef.current = null; };

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: ["places"],
  });

  const user = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("user")); } catch { return null; }
  }, []);

  const [cityMe, setCityMe] = useState(null); // {lat,lng,city,admin1,country,precision}
  const circleOptions = useMemo(() => ({
    strokeOpacity: 0.9,
    strokeWeight: 1,
    fillOpacity: 0.10,
    clickable: false,
    draggable: false,
    editable: false,
    visible: true,
    strokeColor: "#324B8E",
    fillColor: "#324B8E",
  }), []);

  // Translations
  const baseTextByLang = {
    it: {
      introSubtitle: "Scopri le persone nelle vicinanze. Filtra per città o nome. Invia richieste di amicizia.",
      introAuto: "Aggiornamento automatico ogni ~15 s durante la condivisione",
      introPrivacy: "Condivide solo una posizione approssimativa (centro provincia/città)",

      title1: "Trova Italiani nel Mondo",
      title2: "RISCOPRI LE RADICI CULTURALI ITALIANE! TROVA E CONNETTITI CON ITALIANI NEL MONDO",
      title3:
        "Rafforza il senso di comunità, partecipa a uno spazio di discussione e scambio culturale tra gruppi di utenti dedicati a varie tematiche della tradizione italiana come la cucina tipica, storie degli Avi emigrati, la lingua, il folklore, l'artigianato, le tradizioni, feste e rituali, racconti e fiabe, la canzone.",
      shareToggleOn: "Interrompi condivisione",
      shareToggleOff: "Condividi la mia posizione",
      addFriend: "Aggiungi amico",

      fetching: "Recupero persone nelle vicinanze…",
      you: "Tu",
      nearby: "Utenti nelle vicinanze",
      none: "Nessuno online",
      startSharingCta: "Inizia a condividere per interagire con altri utenti online adesso.",
      lastSeen: "Ultima attività",

      meters: "m",
      km: "km",
      radius: "Raggio",

      sort: "Ordina",
      city: "Città",
      searchPlaceholder: "Cerca per nome",
      anyCity: "Città",

      nearest: "Più vicini",
      name: "Nome",
      noneSort: "Nessuno",

      textGia: "Siete già amici.",
      textRichiestaInviata: "Richiesta inviata ✅",
      textNotSent: "Impossibile inviare la richiesta",
      textErroreRete: "Errore di rete.",
    },

    en: {
      introSubtitle: "Discover people nearby. Filter by city or name. Send friend requests.",
      introAuto: "Automatic update every ~15 s while sharing",
      introPrivacy: "Only an approximate position is shared (province/city center)",

      title1: "Find Italians Around the World",
      title2: "REDISCOVER ITALIAN CULTURAL ROOTS! FIND AND CONNECT WITH ITALIANS AROUND THE WORLD",
      title3:
        "Strengthen the sense of community and join spaces for discussion and cultural exchange dedicated to Italian traditions: typical cuisine, stories of emigrated ancestors, language, folklore, crafts, traditions, festivals and rituals, tales and legends, songs.",

      shareToggleOn: "Stop sharing",
      shareToggleOff: "Share my location",
      addFriend: "Add friend",

      fetching: "Loading people nearby…",
      you: "You",
      nearby: "People nearby",
      none: "No one online",
      startSharingCta: "Start sharing to interact with other users online now.",
      lastSeen: "Last activity",

      meters: "m",
      km: "km",
      radius: "Radius",

      sort: "Sort",
      city: "City",
      searchPlaceholder: "Search by name",
      anyCity: "City",

      nearest: "Closest",
      name: "Name",
      noneSort: "None",

      textGia: "You are already friends.",
      textRichiestaInviata: "Friend request sent ✅",
      textNotSent: "Unable to send the request",
      textErroreRete: "Network error.",
    },

    fr: {
      introSubtitle: "Découvrez les personnes à proximité. Filtrez par ville ou nom. Envoyez des demandes d’ami.",
      introAuto: "Mise à jour automatique toutes les ~15 s pendant le partage",
      introPrivacy: "Partage uniquement une position approximative (centre de province/ville)",

      title1: "Trouver des Italiens dans le monde",
      title2: "REDÉCOUVREZ LES RACINES CULTURELLES ITALIENNES ! TROUVEZ ET CONNECTEZ-VOUS AVEC DES ITALIENS DANS LE MONDE",
      title3:
        "Renforcez le sentiment de communauté et participez à des espaces d’échange culturel dédiés aux traditions italiennes : cuisine typique, histoires d’ancêtres émigrés, langue, folklore, artisanat, traditions, fêtes et rituels, contes et chansons.",

      shareToggleOn: "Arrêter le partage",
      shareToggleOff: "Partager ma position",
      addFriend: "Ajouter un ami",

      fetching: "Chargement des personnes à proximité…",
      you: "Vous",
      nearby: "Utilisateurs à proximité",
      none: "Personne en ligne",
      startSharingCta: "Commencez à partager pour interagir avec d’autres utilisateurs en ligne.",
      lastSeen: "Dernière activité",

      meters: "m",
      km: "km",
      radius: "Rayon",

      sort: "Trier",
      city: "Ville",
      searchPlaceholder: "Rechercher par nom",
      anyCity: "Ville",

      nearest: "Les plus proches",
      name: "Nom",
      noneSort: "Aucun",

      textGia: "Vous êtes déjà amis.",
      textRichiestaInviata: "Demande d’ami envoyée ✅",
      textNotSent: "Impossible d’envoyer la demande",
      textErroreRete: "Erreur réseau.",
    },
  };

  const activeBaseText = baseTextByLang[lang] || baseTextByLang.it;
  const [T, setT] = useState(activeBaseText);

  useEffect(() => {
    let cancelled = false;

    // ✅ Fixed texts for Italian / English / French
    if (lang === "it" || lang === "en" || lang === "fr") {
      setT(baseTextByLang[lang] || baseTextByLang.it);
      return;
    }

    // 🌍 Other languages → use Translator starting from Italian base
    const translateAll = async () => {
      try {
        const source = baseTextByLang.it;
        const entries = await Promise.all(
          Object.entries(source).map(async ([k, v]) => {
            if (!v) return [k, v];
            const out = await translateText(v);
            return [k, out || v]; // fallback to Italian if translation empty
          })
        );

        if (!cancelled) {
          setT(Object.fromEntries(entries));
        }
      } catch (err) {
        if (!cancelled) {
          setT(baseTextByLang.it);
        }
      }
    };

    translateAll();

    return () => {
      cancelled = true;
    };
  }, [lang, translateText]);


  const fmtDist = (m) => {
    if (m == null || !isFinite(m)) return "";
    if (m < 1000) return `${Math.round(m)} ${T.meters}`;
    return `${(m / 1000).toFixed(1)} ${T.km}`;
  };

  // --- Reverse geocode to province/city center ----------------------------
  async function geocodeProvinceDowntown({ lat, lng }) {
    const geocoder = new window.google.maps.Geocoder();
    const rev = await new Promise((resolve, reject) => {
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status !== "OK" || !results?.length) return reject(status);
        const pick =
          results.find(r => r.types.includes("administrative_area_level_2")) ||
          results.find(r => r.types.includes("administrative_area_level_1")) ||
          results.find(r => r.types.includes("locality")) ||
          results[0];
        const comps = pick.address_components || [];
        const get = (t) => (comps.find(c => c.types.includes(t))?.long_name) || "";
        const province = get("administrative_area_level_2");
        const admin1   = get("administrative_area_level_1");
        const country  = get("country");
        const loc = pick.geometry?.location;
        const provCenter = {
          lat: loc?.lat?.() ?? lat,
          lng: loc?.lng?.() ?? lng,
        };
        resolve({ province, admin1, country, provCenter });
      });
    });

    const svc = new window.google.maps.places.PlacesService(document.createElement("div"));
    const queries = [];
    const countryLower = (rev.country || "").toLowerCase();
    if (rev.province) {
      queries.push(`${rev.province} city center ${rev.country || ""}`);
      queries.push(`${rev.province} downtown ${rev.country || ""}`);
      queries.push(`${rev.province} capital ${rev.country || ""}`);
      queries.push(`${rev.province} administrative center ${rev.country || ""}`);
      queries.push(`${rev.province} county seat ${rev.country || ""}`);
      if (countryLower === "italia" || countryLower === "italy") {
        queries.push(`${rev.province} capoluogo`);
        queries.push(`centro storico ${rev.province}`);
      }
    }

    const textSearch = (query) =>
      new Promise((resolve) => {
        svc.textSearch(
          { query, location: new window.google.maps.LatLng(rev.provCenter.lat, rev.provCenter.lng), radius: 40000 },
          (places, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK && places?.length) resolve(places[0]);
            else resolve(null);
          }
        );
      });

    for (const q of queries) {
      // eslint-disable-next-line no-await-in-loop
      const hit = await textSearch(q);
      if (hit?.geometry?.location) {
        const p = hit.geometry.location;
        return {
          city: rev.province || rev.admin1 || "",
          admin1: rev.admin1 || "",
          country: rev.country || "",
          lat: p.lat(),
          lng: p.lng(),
          precision: "province_downtown",
        };
      }
    }

    return {
      city: rev.province || rev.admin1 || "",
      admin1: rev.admin1 || "",
      country: rev.country || "",
      lat: rev.provCenter.lat,
      lng: rev.provCenter.lng,
      precision: "province",
    };
  }

  // --- API calls -----------------------------------------------------------
  const shareLocation = throttle(async (pos) => {
    if (!user?.email) return;
    try {
      await fetch(`${BASE}/location_share`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: user.email,
          lat: pos.lat,     // province/city center
          lng: pos.lng,
          accuracy: null,
          city: pos.city || null,
          admin1: pos.admin1 || null,
          country: pos.country || null,
          precision: pos.precision || "city",
          idem: `share:${user.email}:${Math.floor(Date.now() / 15000)}`, // idempotent 15s bucket
        }),
      });
    } catch {
    }
  }, 15000);

  const stopSharing = async () => {
    if (!user?.email) return;
    try {
      await fetch(`${BASE}/location_stop`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: user.email }),
      });
    } catch {}
  };

  const loadNearby = async (center) => {
    if (!user?.email) { setNeighbors([]); return; }
    try {
      const url = new URL(`${BASE}/location_nearby`);
      url.searchParams.set("email", user.email);
      url.searchParams.set("lat", center.lat);
      url.searchParams.set("lng", center.lng);
      url.searchParams.set("radius_km", radiusKm); // true radius
      url.searchParams.set("limit", 100);
      const res = await fetch(url.toString());
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (Array.isArray(data)) setNeighbors(data);
    } catch (e) {
      setToast({ type: "error", message: "Failed to load nearby users." });
    }
  };

  const sendFriendRequest = async (toEmail) => {
    if (!user?.email || !toEmail) return;
    try {
      const res = await fetch(`${BASE}/friends_request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fromEmail: user.email,
          toEmail,
          dedupeKey: `fr:${user.email}:${toEmail}`,
        }),
      });
      const out = await res.json();
      if (out?.alreadyFriends) {
        setToast({ type: "info", message: T.textGia });
      } else if (out?.status === "ok") {
        setToast({ type: "success", message: T.textRichiestaInviata });
      } else {
        setToast({ type: "error", message: out?.message || T.textNotSent });
      }
    } catch {
      setToast({ type: "error", message: T.textErroreRete });
    }
  };


  // --- LIFECYCLE -----------------------------------------------------------
  // Initial geolocation (single get)
  useEffect(() => {
    if (!navigator.geolocation) { setLoading(false); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const c = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setMapCenter(c);
        setMe({ ...c, accuracy: pos.coords.accuracy });
        setLoading(false);
      },
      () => setLoading(false),
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 10000 }
    );
  }, []);

  // Start/stop a SINGLE watchPosition when sharing toggles
  useEffect(() => {
    if (!navigator.geolocation) return;

    if (sharing && !watchingRef.current) {
      watchingRef.current = true;
      const id = navigator.geolocation.watchPosition(
        async (pos) => {
          const p = { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy };
          setMe(p);
          if (!isLoaded || !window.google) return;

          try {
            const prov = await geocodeProvinceDowntown(p);
            setCityMe(prov);

            if (!didCenterRef.current) {
              setMapCenter({ lat: prov.lat, lng: prov.lng });
              didCenterRef.current = true;
            }
            shareLocation(prov); // throttled
          } catch {
            // Fallback (1km grid)
            const qlat = Math.round(p.lat * 100) / 100;
            const qlng = Math.round(p.lng * 100) / 100;
            const fallback = { lat: qlat, lng: qlng, city: null, admin1: null, country: null, precision: "grid_1km" };
            setCityMe(fallback);
            if (!didCenterRef.current) {
              setMapCenter({ lat: qlat, lng: qlng });
              didCenterRef.current = true;
            }
            shareLocation(fallback);
          }
        },
        (err) => {
          setToast({ type: "error", message: "Location permission denied or unavailable." });
          console.warn("geo error", err);
        },
        { enableHighAccuracy: true, maximumAge: 10000, timeout: 20000 }
      );
      // Store id for cleanup
      watchingRef.current = id;
    }

    if (!sharing && watchingRef.current) {
      try { navigator.geolocation.clearWatch(watchingRef.current); } catch {}
      watchingRef.current = false;
      stopSharing();

      // Reset UI state (keep current map view)
      setCityMe(null);
      setNeighbors([]);
      setActiveUser(null);
      didCenterRef.current = false;

      if (circleRef.current) { try { circleRef.current.setMap(null); } catch {} circleRef.current = null; }
    }

    return () => {
      if (watchingRef.current) {
        try { navigator.geolocation.clearWatch(watchingRef.current); } catch {}
        watchingRef.current = false;
      }
    };
  }, [sharing, isLoaded]);

  // Draw/update radius circle (true radius)
  useEffect(() => {
    if (!isLoaded || !sharing || !cityMe || !radiusKm || !mapRef.current || !window.google) {
      if (circleRef.current) { try { circleRef.current.setMap(null); } catch {} circleRef.current = null; }
      return;
    }

    const center = new window.google.maps.LatLng(cityMe.lat, cityMe.lng);
    const radiusMeters = radiusKm * 1000;

    if (!circleRef.current) {
      circleRef.current = new window.google.maps.Circle({
        map: mapRef.current,
        center,
        radius: radiusMeters,
        ...circleOptions,
      });
    } else {
      circleRef.current.setCenter(center);
      circleRef.current.setRadius(radiusMeters);
    }
  }, [sharing, cityMe?.lat, cityMe?.lng, radiusKm, isLoaded, circleOptions]);

  // Poll neighbors every 15s while sharing & we have a center
  useEffect(() => {
    let stop = false;
    (async function loop() {
      if (stop) return;
      if (sharing && cityMe) {
        await loadNearby({ lat: cityMe.lat, lng: cityMe.lng });
      }
      setTimeout(loop, 15000);
    })();
    return () => { stop = true; };
  }, [sharing, cityMe?.lat, cityMe?.lng, radiusKm, user?.email]);

  // --- DERIVED DATA --------------------------------------------------------
  const cityOptions = useMemo(() => {
    const set = new Set();
    neighbors.forEach(n => {
      const label = [n.city, n.admin1, n.country].filter(Boolean).join(", ");
      if (label) set.add(label);
    });
    return Array.from(set).sort();
  }, [neighbors]);

  const filteredNeighbors = useMemo(() => {
    let list = neighbors.slice();

    if (cityFilter) {
      list = list.filter(n => {
        const label = [n.city, n.admin1, n.country].filter(Boolean).join(", ");
        return label === cityFilter;
      });
    }

    if (debouncedQuery.trim()) {
      const q = debouncedQuery.trim().toLowerCase();
      list = list.filter(n => (n.name || "").toLowerCase().includes(q));
    }

    if (sortBy === "nearest" && cityMe) {
      const here = { lat: cityMe.lat, lng: cityMe.lng };
      list.sort((a, b) =>
        distanceMeters(here, { lat: a.lat, lng: a.lng }) -
        distanceMeters(here, { lat: b.lat, lng: b.lng })
      );
    } else if (sortBy === "name") {
      list.sort((a, b) => (a.name || a.email || "").localeCompare(b.name || b.email || ""));
    }

    return list;
  }, [neighbors, cityFilter, debouncedQuery, sortBy, cityMe]);

  // --- RENDER --------------------------------------------------------------
  const myMarker = sharing && cityMe && (
    <Marker
      position={{ lat: cityMe.lat, lng: cityMe.lng }}
      title={T.you}
    />
  );

  return (
    <div className="w-full bg-white text-neutral-900">
      {/* Hero */}
      <header
        className="bg-[#324B8E] w-full max-w-[1920px] mx-auto px-6 md:px-16
                   pt-[max(1rem,env(safe-area-inset-top))]
                   pb-[max(1rem,env(safe-area-inset-bottom))]
                   flex items-center min-h-[220px] md:min-h-[300px]"
      >
        <h1 className="text-white text-2xl sm:text-3xl font-bold"
          /*className="font-[Montserrat] font-bold text-white
                     text-[1.75rem] sm:text-[2.5rem] md:text-[3rem] lg:text-[3.5rem] xl:text-[4rem] leading-[1.1]
                     max-w-[92vw] sm:max-w-[90%] lg:max-w-[1100px] xl:max-w-[1200px]"*/
        >
          {T.title3}
        </h1>
      </header>

    <div className="max-w-8xl mx-auto px-3 sm:px-6 pb-10">
      {/* Intro */}
      <div className="mt-6 mb-4 rounded-2xl border bg-white/60 p-4 sm:p-6 shadow-sm">
        <div className="flex items-center gap-3">
          <Users className="w-6 h-6 text-[#324B8E]" />
          <h2 className="text-black text-2xl sm:text-3xl font-bold">{T.title2}</h2>
        </div>
        <p className="mt-2 text-lg text-gray-600">{T.introSubtitle}</p>
        <div className="mt-2 flex flex-wrap gap-2 text-xs text-gray-500">
          <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-1">
            <Info className="w-3.5 h-3.5" /> {T.introAuto}
          </span>
          <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-1">
            <Info className="w-3.5 h-3.5" /> {T.introPrivacy}
          </span>
        </div>
      </div>

      {/* Controls row */}
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <button
          onClick={() => setSharing(s => !s)}
          className={`px-4 py-2 rounded-xl text-white flex items-center gap-2 ${sharing ? "bg-red-600" : "bg-[#324B8E]"}`}
          aria-pressed={sharing}
        >
          <LocateFixed className="w-5 h-5" />
          {sharing ? T.shareToggleOn : T.shareToggleOff}
        </button>

        <div className="ml-auto flex items-center gap-2 text-sm">
          <span className="hidden sm:inline">{T.radius}:</span>
          <nav aria-label={T.radius} className="-mx-1">
            <div className="flex gap-2 flex-nowrap overflow-x-auto ios-scroll scrollbar-none px-1 py-1 md:flex-wrap md:overflow-visible">
              {[5,10,20,50,100].map(r => (
                <button
                  key={r}
                  onClick={() => setRadiusKm(r)}
                  className={
                    "px-3 py-1.5 rounded-full text-sm border transition " +
                    (radiusKm === r
                      ? "bg-[#324B8E] text-white border-[#324B8E]"
                      : "text-[#324B8E] border-[#324B8E] hover:bg-[#324B8E]/10")
                  }
                  aria-pressed={radiusKm === r}
                >
                  {r} km
                </button>
              ))}
            </div>
          </nav>
        </div>
      </div>

      {/* Layout: Map + Panel */}
      <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-4 mt-4">
        {/* MAP */}
        <section className="rounded-2xl overflow-hidden border bg-white shadow-sm">
          <div className="w-full h-[60vh] sm:h-[68vh] lg:h-[72vh]">
            {loadError && (
              <div className="h-full flex items-center justify-center text-red-600 text-sm">
                Map failed to load.
              </div>
            )}
            {isLoaded && (
              <GoogleMap
                mapContainerStyle={containerStyle}
                center={mapCenter}
                zoom={11}
                onLoad={onMapLoad}
                onUnmount={onMapUnmount}
                options={{
                  mapTypeControl: false,
                  streetViewControl: false,
                  fullscreenControl: true,
                  gestureHandling: "greedy",
                  clickableIcons: false,
                }}
              >
                {myMarker}

                {/* Cluster neighbors for performance */}
                {sharing && filteredNeighbors.length > 0 && (
                  <MarkerClustererF averageCenter enableRetinaIcons>
                    {(clusterer) =>
                      filteredNeighbors.map((u) => (
                        <Marker
                          key={u.email}
                          position={{ lat: u.lat, lng: u.lng }}
                          clusterer={clusterer}
                          onClick={() => {
                            setActiveUser(u);
                            // center map on click for clarity
                            if (mapRef.current) {
                              mapRef.current.panTo({ lat: u.lat, lng: u.lng });
                            }
                          }}
                          title={u.name || u.email}
                        />
                      ))
                    }
                  </MarkerClustererF>
                )}

                {activeUser && (
                  <InfoWindow
                    position={{ lat: activeUser.lat, lng: activeUser.lng }}
                    onCloseClick={() => setActiveUser(null)}
                  >
                    <div className="max-w-[240px]">
                      <div className="flex items-center gap-2 mb-2">
                        <img
                          src={activeUser.image || "/avatar.png"}
                          alt=""
                          className="w-8 h-8 rounded-full object-cover"
                        />
                        <div>
                          <div className="font-semibold leading-tight">
                            {activeUser.name || activeUser.email}
                          </div>
                          <div className="text-xs text-gray-500">
                            {[activeUser.city, activeUser.admin1, activeUser.country].filter(Boolean).join(", ")}
                          </div>
                          <div className="text-xs text-gray-500">
                            {T.lastSeen}: {activeUser?.updatedAt ? new Date(activeUser.updatedAt).toLocaleString() : "—"}
                          </div>
                        </div>
                      </div>
                      <button
                        className="w-full inline-flex items-center justify-center gap-2 bg-[#324B8E] text-white px-3 py-1.5 rounded-lg"
                        onClick={() => sendFriendRequest(activeUser.email)}
                      >
                        <UserPlus className="w-4 h-4" /> {T.addFriend}
                      </button>
                    </div>
                  </InfoWindow>
                )}
              </GoogleMap>
            )}
          </div>
        </section>

        {/* PANEL */}
        <aside className="rounded-2xl border bg-white shadow-sm p-3 sm:p-4 lg:sticky lg:top-4 h-[60vh] sm:h-[68vh] lg:h-[72vh] overflow-auto ios-scroll scrollbar-none">
          <div className="flex items-center gap-2 text-lg font-semibold">
            <MapPin className="w-5 h-5 text-[#324B8E]" />
            <span>{T.nearby}</span>
          </div>

          {/* Filter chips row */}
          <nav className="mt-3 -mx-1" aria-label="Filters">
            <div className="flex gap-2 flex-nowrap overflow-x-auto ios-scroll scrollbar-none px-1 py-1 md:flex-wrap md:overflow-visible">
              {/* Sort chips */}
              {[
                {k:"nearest",t:T.nearest, disabled: !cityMe},
                {k:"name",t:T.name},
                {k:"none",t:T.noneSort},
              ].map(o=>(
                <button
                  key={o.k}
                  onClick={()=> setSortBy(o.k)}
                  disabled={o.disabled}
                  className={
                    "px-3 py-1.5 rounded-full text-sm border transition " +
                    (sortBy===o.k
                      ? "bg-[#324B8E] text-white border-[#324B8E]"
                      : "text-[#324B8E] border-[#324B8E] hover:bg-[#324B8E]/10 disabled:opacity-50")
                  }
                  aria-pressed={sortBy===o.k}
                >
                  {o.t}
                </button>
              ))}
            </div>
          </nav>

          {/* Secondary filters */}
          <div className="w-full mt-2 grid grid-cols-1 md:grid-cols-3 gap-2">
            {/* City */}
            <label className="flex items-center gap-2 text-sm">
              <select
                className="border rounded-md px-2 py-1 min-w-0 flex-1"
                value={cityFilter}
                onChange={e => setCityFilter(e.target.value)}
              >
                <option value="">{T.anyCity}</option>
                {cityOptions.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>

            {/* Seen within 
            <label className="flex items-center gap-2 text-sm">
              <select
                className="border rounded-md px-2 py-1 min-w-0 flex-1"
                value={seenWithin}
                onChange={e => setSeenWithin(e.target.value)}
              >
                <option value="any">{T.anyTime}</option>
                <option value="5m">{T.last5m}</option>
                <option value="1h">{T.lastHour}</option>
                <option value="24h">{T.last24h}</option>
              </select>
            </label>*/}

            {/* Name search */}
            <input
              className="border rounded-md px-3 py-1 text-sm"
              placeholder={T.searchPlaceholder}
              value={nameQuery}
              onChange={e => setNameQuery(e.target.value)}
            />
          </div>

          {/* List */}
          {filteredNeighbors.length === 0 ? (
            <p className="text-gray-600 mt-3">{loading ? T.fetching : (sharing ? T.none : T.startSharingCta)}</p>
          ) : (
            <ul className="mt-3 space-y-2 max-h-[calc(100%-120px)] overflow-auto pr-1 ios-scroll scrollbar-none">
              {sharing && filteredNeighbors.map(u => {
                const dist = cityMe ? fmtDist(distanceMeters(
                  { lat: cityMe.lat, lng: cityMe.lng }, { lat: u.lat, lng: u.lng }
                )) : "";
                return (
                  <li
                    key={u.email}
                    className="flex items-center gap-3 p-2 rounded-xl border hover:bg-gray-50 hover:shadow-sm hover:-translate-y-0.5 transition"
                  >
                    <button
                      className="flex items-center gap-3 text-left flex-1 min-w-0"
                      onClick={() => {
                        setActiveUser(u);
                        if (mapRef.current) mapRef.current.panTo({ lat: u.lat, lng: u.lng });
                      }}
                    >
                      <img src={u.image || "/avatar.png"} alt="" className="w-10 h-10 rounded-full object-cover" />
                      <div className="min-w-0 flex-1">
                        <div className="font-medium truncate">{u.name || u.email}</div>
                        <div className="text-xs text-gray-500 truncate">
                          {[u.city, u.admin1, u.country].filter(Boolean).join(", ")}
                        </div>
                        <div className="text-[11px] text-gray-400 flex items-center gap-2">
                          {dist && <span aria-hidden>•</span>}
                          {dist && <span>{dist}</span>}
                        </div>
                      </div>
                    </button>
                    <button
                      className="shrink-0 bg-[#324B8E] text-white px-3 py-1.5 rounded-full text-sm"
                      onClick={() => sendFriendRequest(u.email)}
                    >
                      {T.addFriend}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </aside>
      </div>

      {/* Toasts */}
      {toast && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[1000]">
          <div
            className={
              "flex items-center gap-2 rounded-xl px-4 py-2 shadow-lg " +
              (toast.type === "error"
                ? "bg-red-600 text-white"
                : toast.type === "success"
                ? "bg-emerald-600 text-white"
                : "bg-gray-900 text-white")
            }
            role="status"
            aria-live="polite"
          >
            {toast.type === "error" ? <XCircle className="w-4 h-4" /> : <Info className="w-4 h-4" />}
            <span className="text-sm">{toast.message}</span>
            <button className="ml-2 text-white/80 hover:text-white" onClick={() => setToast(null)}>×</button>
          </div>
        </div>
      )}
    </div>
  </div>
  );
}
